import moment from 'moment';

import { INSIGHT_REPORT_TYPE, CALLLOG_REPORT_TYPE } from './state';
import { getDefaultChartParameters } from '../services/ReportingService';
import { rangeMethods, timeIntervalToValueMapping } from '@/data/getDateRanges';
import {
  convertApi2AgGridColDef,
  convertTag2AgGridColDef,
  convertApiColDefs2Filters,
  convertColsToGroupBys,
  convertTagsToGroupBys,
  convertApi2AgGridColDefForCallLogs,
  groupCallLogsColDefs,
  groupInsightsColDefs,
  HEADER_NAME_COLUMNS,
  checkTimeInterval
} from '@/modules/reporting/services/ReportingService';
import { dateTimeService } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import { groupTags } from '@/data/defaultTags';
import { getParameterCaseInsensitive } from '@/core/utilities';

const callLogsQuickFiltersFieldNames = [
  'campaignName',
  'targetName',
  'buyerTargetName',
  'buyer',
  'numberPoolName',
  'publisherName',
  'number',
  'inboundCallId',
  'isDuplicate',
  'inboundPhoneNumber'
];

const insightsQuickFiltersFieldNames = [
  'campaignName',
  'targetName',
  'buyerTargetName',
  'buyer',
  'numberPoolName',
  'publisherName',
  'number',
  'isDuplicate',
  'inboundPhoneNumber'
];

// const buyerMiscFilters = [{
//   id: 'hasConverted',
//   filterGroupName: 'Performance',
//   supportsFilter: true,
//   fieldType: 'boolean',
//   isTag: false,
//   roles: ['buyer'],
//   title: 'Paid Out',
// },
// {
//   id: 'hasConnected',
//   filterGroupName: 'Call',
//   supportsFilter: true,
//   fieldType: 'boolean',
//   isTag: false,
//   roles: ['buyer'],
//   title: 'Connected',
// },
// {
//   id: 'isIncomplete',
//   filterGroupName: 'Call',
//   supportsFilter: true,
//   fieldType: 'boolean',
//   isTag: false,
//   roles: ['buyer'],
//   title: 'Completed',
// }];

function _timeRange(dateRangeState, timeZoneId) {
  if (!timeZoneId) {
    timeZoneId = moment.tz.guess();
  }

  if (!dateRangeState.isCustomRange) {
    return getParameterCaseInsensitive(rangeMethods, dateRangeState.quickRangeSelector)(timeZoneId);
  }

  if (dateRangeState.timeRange && dateRangeState.timeRange.length === 2) {
    return [moment(dateRangeState.timeRange[0]), moment(dateRangeState.timeRange[1])];
  }

  return [moment().startOf('day'), moment().endOf('day')];
}

function transformFilterCriteriaForMigratedReports(insight, allFilters) {
  if (!insight || !insight.id?.endsWith('-m') || insight.reportType !== CALLLOG_REPORT_TYPE) {
    return insight.settings.filterCriteria;
  }

  let filterCriteria = insight.settings.filterCriteria;
  let newFilterCriteria = [];

  if (filterCriteria) {
    filterCriteria.forEach((andCriteria) => {
      let newAndCriteria = { tagCriteria: [] };
      andCriteria.tagCriteria.forEach((orCriteria) => {
        if (orCriteria.tagId.includes('Migrated:')) {
          let tagId = orCriteria.tagId.split('Migrated:')[1];

          let foundTag = allFilters.find((tag) => {
            return tag.tagName === tagId;
          });

          if (foundTag) {
            let newOrCriteria = {
              ...orCriteria,
              tagId: `${foundTag.tagType}:${foundTag.tagName}`
            };

            newAndCriteria.tagCriteria.push(newOrCriteria);
          } else {
            newAndCriteria.tagCriteria.push(orCriteria);
          }

        } else {
          newAndCriteria.tagCriteria.push(orCriteria);
        }
      });

      newFilterCriteria.push(newAndCriteria);
    });
  }

  return newFilterCriteria;
}

export const role = (state, getters, rootState, rootGetters) => {
  // This is added to fix this:https://app.shortcut.com/ringba/story/8149/fix-bug-when-we-change-reporting-page-to-other-view-different-than-admin-then-go-to-insights-page-the-data-does-not-loadhttps://app.shortcut.com/ringba/story/8149/fix-bug-when-we-change-reporting-page-to-other-view-different-than-admin-then-go-to-insights-page-the-data-does-not-load
  // The role is used to filter out insightsColumnDefinitionsWithoutTags, which is used in both `Quick Insights` in Call Log and `Insights`
  // Insights is only shown to admins, the role here becomes irrelevant for insights
  if (state.currentView === INSIGHT_REPORT_TYPE) {
    return 'admin';
  }
  
  if (rootGetters.isBuyer || (rootGetters.isBuyerGroupUser && !rootGetters.inAdminReportViewGroup)) {
    return 'buyer';
  }
  
  if (rootGetters.isPublisher || (rootGetters.isPublisherGroupUser && !rootGetters.inAdminReportViewGroup)) {
    return 'publisher';
  }

  if ((rootGetters.isBuyerGroupUser || rootGetters.isPublisherGroupUser) && rootGetters.inAdminReportViewGroup) {
    return 'admin';
  }
  
  if (rootGetters.isReadOnlyReportUser || rootGetters.isAdmin) {
    let role = rootGetters.isAdmin ? 'admin' : 'user';

    if (getters.activeCallLogViewAsBuyerId) {
      return 'buyer';
    }
    if (getters.activeCallLogViewAsPubId) {
      return 'publisher';
    }
    if (getters.activeCallLogViewAsManagerId) {
      if (getters.activeCallLogViewAs.adminReportView) {
        return role;
      }
      if (getters.activeCallLogViewAs.type == 'Affiliate') {
        return 'publisher';  
      }
      if (getters.activeCallLogViewAs.type == 'Buyer') {
        return 'buyer';  
      }
    }
    return role;
  }
  
  return 'error';
};

export const chartParameters = () => {
  return getDefaultChartParameters();
};

export const histogramTimeGroupInterval = (state, getters) => {
  const activeCallLogSettings = getters.activeCallLog.settings;

  if (activeCallLogSettings.histogramSettings.groupTimeInterval !== 'auto') {
    return activeCallLogSettings.histogramSettings.groupTimeInterval;
  }

  const [reportStart, reportEnd] = getters.activeCallLogTimeRange;

  const diff = moment.duration(reportEnd.diff(reportStart));
  let timeInterval = checkTimeInterval(diff);

  return timeIntervalToValueMapping[timeInterval].code;
};

export const chartSelectedParameters = state => {
  const settings = activeInsight(state).settings;
  return settings.groupSettings?.chartStates?.selectedParameters;
};

export const savedInsights = state => state.savedInsights.filter(insight => !insight.reportType || insight.reportType === INSIGHT_REPORT_TYPE);

export const savedCallLogs = state => state.savedInsights.filter(insight => insight.reportType === CALLLOG_REPORT_TYPE);

export const selectedChartType = state => activeInsight(state).settings.selectedChartType;

export const callLogColumns = (state, getters) => {
  let colDefs = [];
  const role = getters.role;

  if (role == 'admin' || role == 'user') {
    colDefs = [
      ...state.callLog.colDefs
        .filter(c => c.roles?.includes(role))
        .map(apiColDef => convertApi2AgGridColDefForCallLogs(apiColDef, role)),
      ...groupTags(state.tags)
    ];
  }
  
  else if (role == 'publisher') {
    colDefs = [
      ...state.callLog.colDefs
        .filter(c => c.roles?.includes('affiliate'))
        .map(apiColDef => convertApi2AgGridColDefForCallLogs(apiColDef, role)),
      ...groupTags(state.tags)
    ];
  }
  
  else if (role == 'buyer') {
    colDefs = [
      ...state.callLog.colDefs
        .filter(c => c.roles?.includes('buyer'))
        .map(apiColDef => convertApi2AgGridColDefForCallLogs(apiColDef, role)),
    ];
  }
  // If colDefs exists, look for and filter out duplicates
  if (colDefs) {
    colDefs = colDefs.map(cd => {
      if (cd.children) {
        const childTagIds = cd.children.map(c => c.colId)
        const filteredChildren = cd.children.filter(({ colId }, index) => !childTagIds.includes(colId, index + 1))
        cd.children = filteredChildren;
      }
      return cd;
    })
  }
  return groupCallLogsColDefs(colDefs);
};

export const insightColumnDefinitionsWithoutTags = (state, getters) => {
  const apiRole = getters.role === 'publisher' ? 'affiliate' : getters.role;
  const colTypeFlex = {
    money: 72,
    timespan: 71,
    long: 69,
    percent: 68
  };
  const colTypeMinWidths = {
    money: 95,
    timespan: 85,
    long: 60,
    percent: 58
  };

  const minWidth = (col) => colTypeMinWidths[col.type] || 70;
  const flex = (col) => colTypeFlex[col.type] || 70;

  return [
    ...state.insight.colDefs
      .filter(c => c.roles?.includes(apiRole) ?? false)
      .map(apiColDef => convertApi2AgGridColDef(apiColDef, minWidth(apiColDef), flex(apiColDef), getters.role))
  ];
};

export const insightColumnDefinitions = (state, getters) => {
  return [
    ...getters.insightColumnDefinitionsWithoutTags,
    ...state.tags.map(t => convertTag2AgGridColDef(t))
  ];
};

export const insightColumnDefLookup = (state, getters) => {
  const colDefs = getters.insightColumnDefinitions;
  const lookUp = {};
  
  if (!colDefs.length) {
    return lookUp;
  }

  colDefs.forEach(c => {
    if (c.colId) {
      lookUp[c.colId] = c;
    }
  });

  return lookUp;
};

export const insightColumns = (state, getters) => {
  const defs = getters.insightColumnDefinitions;
  const cols = groupInsightsColDefs(defs).find(c => c.headerName === HEADER_NAME_COLUMNS);

  if (cols && cols.children) {
    return [
      cols
    ];
  }

  return [];
};

// TODO: Create getter for filters, I don't think we need them in state.
export const availableFilters = (state, getters) => {
  let role = getters.role === 'publisher' ? 'affiliate' : getters.role;

  let colDefs = state.insight.colDefs.filter(col => col.roles?.includes(role));
  return [
    ...state.tags.map(t => ({ ...t, tagSource: 'Tags', fieldName: t.tagName, isTag: true })),
    ...convertApiColDefs2Filters(colDefs, getters.role, INSIGHT_REPORT_TYPE)
  ];
};

export const insightsQuickFilters = (state, getters, rootState, rootGetters) => {
  let quickFilters = [];
  const apiRole = getters.role === 'publisher' ? 'affiliate' : getters.role;
  
  insightsQuickFiltersFieldNames.forEach((field) => {
    // Add the f.tagSource==='Filters' to prevent picking userTags named exactly like the predefined filter tags
    let columnDef = getters.availableFilters.find(f => f.fieldName === field && f.tagSource === 'Filters');

    if (columnDef && (rootGetters.isAdmin || !columnDef.roles || columnDef.roles.includes(apiRole))) {
      quickFilters.push({
        tagId: `${columnDef.tagType}:${columnDef.tagName}`,
        displayName: columnDef.displayName,
        field: field
      });
    }
  });

  return quickFilters;
};


export const availableGroupBys = (state, getters) => {
  let role = getters.role === 'publisher' ? 'affiliate' : getters.role;

  let colDefs = state.insight.colDefs.filter(col => col.roles?.includes(role));

  return {
    columns: convertColsToGroupBys(colDefs),
    tags: convertTagsToGroupBys(state.tags)
  };
};

export const activeInsight = state => state.insight.reports[state.insight.activeId];

export const activeInsightData = state => state.insight.data[state.insight.activeId];

export const activeInsightSelectedGroupBy = state => activeInsight(state).settings.selectedGroupBy;

export const activeInsightFilterCriteria = state => activeInsight(state).settings.filterCriteria;

export const activeInsightGroupHierarchy = state => {
  return activeInsight(state).settings.groupSettings?.gridState.rowGroupCols;
};

export const activeInsightTimeZoneId = state => activeInsight(state).settings.dateRange.timeZoneId;

export const activeInsightTranslatedTimeZoneId = state => {
  return dateTimeService.getTimeZoneMapping(activeInsightTimeZoneId(state));
};

export const activeInsightLastUpdated = state => {
  return activeInsightData(state)?.lastUpdated;
};

export const activeInsightTimeRange = (state, getters) => {
  return _timeRange(activeInsight(state).settings.dateRange, getters.activeInsightTranslatedTimeZoneId);
};


export const activeCallLog = state => state.callLog.reports[state.callLog.activeId];

export const activeCallLogData = state => state.callLog.data[state.callLog.activeId];

export const activeCallLogTimeZoneId = (state, getters) => getters.activeCallLog.settings.dateRange.timeZoneId;

export const activeCallLogTranslatedTimeZoneId = (state, getters) => dateTimeService.getTimeZoneMapping(getters.activeCallLogTimeZoneId);

export const activeCallLogLastUpdated = (state, getters) => getters.activeCallLogData.lastUpdated;

export const activeCallLogTimeRange = (state, getters) => _timeRange(getters.activeCallLog.settings.dateRange, getters.activeCallLogTranslatedTimeZoneId);

export const availableCallLogsFilters = (state, getters) => {
  const apiRole = getters.role === 'publisher' ? 'affiliate' : getters.role;
  let miscFilters = [];

  let filters = [
    ...state.tags.map(t => ({ ...t, tagSource: 'Tags', fieldName: t.tagName, isTag: true })),
    ...convertApiColDefs2Filters(state.callLog.colDefs, getters.role, CALLLOG_REPORT_TYPE).filter(f => !f.roles || f.roles.includes(apiRole)),
  ];

  // if (apiRole === 'buyer') {
  //   //miscFilters = convertApiColDefs2Filters(buyerMiscFilters, getters.role, CALLLOG_REPORT_TYPE);
  // }

  return filters.concat(miscFilters);
};

export const callLogsQuickFilters = (state, getters) => {
  let quickFilters = [];
  const apiRole = getters.role === 'publisher' ? 'affiliate' : getters.role;

  callLogsQuickFiltersFieldNames.forEach((field) => {
    // Add the f.tagSource==='Filters' to prevent picking userTags named exactly like the predefined filter tags
    let columnDef = getters.availableCallLogsFilters.find(f => f.fieldName === field && f.tagSource === 'Filters');
    
    if (columnDef && (role === 'admin' || !columnDef.roles || columnDef.roles.includes(apiRole))) {
      quickFilters.push({
        tagId: `${columnDef.tagType}:${columnDef.tagName}`,
        displayName: columnDef.displayName,
        field: field
      });
    }
  });

  return quickFilters;
};

export const activeCallLogFilterCriteria = (state, getters) => {
  return transformFilterCriteriaForMigratedReports(getters.activeCallLog, getters.availableCallLogsFilters);
};

export const activeCallLogViewAs = (state, getters) => getters.activeCallLog.settings.viewAs;

export const activeCallLogViewAsPubId = (state, getters) => {
  if (getters.activeCallLog?.settings?.viewAs?.id?.startsWith('AF')) {
    return getters.activeCallLog.settings.viewAs.id;
  }
  return;
};

export const activeCallLogViewAsBuyerId = (state, getters) => {
  if (getters.activeCallLog?.settings?.viewAs?.id?.startsWith('BU')) {
    return getters.activeCallLog.settings.viewAs.id;
  }
  return;
};

export const activeCallLogViewAsManagerId = (state, getters) => {
  if (getters.activeCallLog?.settings?.viewAs?.id?.startsWith('MGR')) {
    return getters.activeCallLog.settings.viewAs.id;
  }
  return;
};

export const canDisputeConversions = (state, getters, rootState, rootGetters) => {
  if (rootGetters.isAdmin) {
    return true;
  }

  const grantedPermissions = (rootGetters.isBuyer && rootState.currentAccountUser?.grantedPermissions) || [];

  return grantedPermissions.includes('dispute_conversions');
};

export const canExportReport = (state, getters, rootState, rootGetters) => {
  return !rootGetters.isReadOnlyReportUser || (rootGetters.isReadOnlyReportUser && rootGetters.hasAccessToItem('CallLogFileExportJob', 'CREATE'));
};